import { Badge } from "@/components/ui/badge";
import { readingLabel, autoReadingNote } from "@/lib/reading-labels";

import { Button } from "@/components/ui/button";
import { WhyDisabled } from "@/components/ui/disabled-reason";
import { Separator } from "@/components/ui/separator";
import type { PaperTag, PaperWithRole } from "@/types/paper";
import type { FacetSummary } from "@/types/concept";
import { angleChips } from "@/lib/angles";
import { safeDoiHref, safeExternalHref } from "@/lib/safeUrl";
import {
  ArrowRightLeft,
  ArrowUpRight,
  BookOpen,
  Check,
  ChevronDown,
  ChevronRight,
  Copy,
  ExternalLink,
  FileText,
  Flag,
  Loader2,
  RefreshCw,
  RotateCcw,
  Maximize2,
  Minimize2,
  Sparkles,
  ThumbsDown,
  ThumbsUp,
  X,
} from "lucide-react";
import { CrossConceptSuggestions } from "./CrossConceptSuggestions";
import { DismissPopover } from "./DismissPopover";
import { FlagPopover } from "./FlagPopover";
import { RouteToConceptPopover } from "./RouteToConceptPopover";
import { DiscoveryProvenance } from "./DiscoveryProvenance";
import { PdfRow } from "./PdfRow";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { useCallback, useEffect, useRef, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Tag, Plus, Pencil } from "lucide-react";


const READING_STATUSES = ["unread", "skimmed", "read"] as const;
const TAG_COLORS = [
  "#3B82F6", "#10B981", "#F59E0B", "#EF4444",
  "#8B5CF6", "#EC4899", "#F97316", "#6B7280",
];

interface Props {
  paper: PaperWithRole;
  /** Facets 1c-2: active angles (pass only at 2+) for the Why it's here chips. */
  angles?: FacetSummary[];
  conceptId?: string;
  onClose: () => void;
  onPromote?: (paperId: string) => void;
  onDownloadPdf?: (paperId: string) => void;
  onViewPdf?: (paperId: string) => void;
  onExpandPdf?: (paperId: string) => void;
  onExtractAbstract?: (paperId: string) => void;
  onUploadPdf?: (paperId: string, file: File) => void;
  /** Opens Settings → Integrations from the PDF row's "Set up your library link". */
  onSetUpLibraryLink?: () => void;
  onScoreOverride?: (paperId: string, score: number | null) => void;
  onSummarize?: (paperId: string) => void;
  /** Re-run summarize with force_user=true — used when the current summary is
   * canonical but the user has uploaded a private PDF. */
  onRegenerateFromUpload?: (paperId: string) => void;
  isSummarizing?: boolean;
  /** Source of the current paper.ai_summary — 'user' for user-upload-derived,
   * 'canonical' for canonical full-text-derived. */
  summarySource?: "user" | "canonical";
  /** True when the user has uploaded their own PDF for this paper (source="user"
   * on the PDF resolver). Controls visibility of "Regenerate from your upload". */
  hasUserUpload?: boolean;
  onReadingStatus?: (paperId: string, status: string) => void;
  onNotesChange?: (paperId: string, notes: string) => void;
  availableTags?: PaperTag[];
  onUpdateTags?: (paperId: string, tagIds: string[]) => void;
  onCreateTag?: (data: { name: string; color: string }) => void;
  onUpdateTag?: (tagId: string, data: { name?: string; color?: string }) => void;
  onDismiss?: (paperId: string, reason: string, reasonText?: string, findSimilar?: boolean) => void;
  findSimilarDisabled?: boolean;
  onRestore?: (paperId: string) => void;
  onRelevanceNoteChange?: (paperId: string, note: string) => void;
  onFlag?: (paperId: string, note?: string) => void;
  isRetrieving?: boolean;
  isUploading?: boolean;
  viewMode?: "info" | "pdf" | "fulltext";
  pdfViewerContent?: React.ReactNode;
  fullTextViewerContent?: React.ReactNode;
  explanationContent?: React.ReactNode;
  onClosePdfViewer?: () => void;
  onViewFullText?: (paperId: string) => void;
  onMaximizePdf?: () => void;
  pdfMaximized?: boolean;
  onSmartTrawl?: () => void;
  smartTrawlRunning?: boolean;
  hasHighlights?: boolean;
  /** Persistent evidence clips (source='evidence' PaperHighlights) taken from
   * this paper — shown so Curate answers "what have I already pulled from
   * this paper?" without opening the Evidence Map. */
  evidenceClips?: Array<{ id: string; text: string; page: number | null }>;
  /** Open the PDF at a clip's page and focus its highlight. */
  onViewClip?: (page: number | null, highlightId: string) => void;
  /** True while the parent is fetching the full PaperWithRole on top of a slim
   * row. Drives skeletons for abstract / scoring rationale and a "Loading
   * notes…" caption so empty heavy fields don't read as "no data". */
  isHydrating?: boolean;
  /** id → paper for this concept (seeds + found papers); enables the
   * "How PaperChase found this" provenance line. */
  paperIndex?: Map<string, PaperWithRole>;
}

export function PaperDetail({
  paper,
  conceptId,
  onClose,
  onPromote,
  onDownloadPdf,
  onViewPdf,
  onExpandPdf,
  onExtractAbstract,
  onUploadPdf,
  onSetUpLibraryLink,
  onScoreOverride,
  onSummarize,
  onRegenerateFromUpload,
  isSummarizing = false,
  summarySource,
  hasUserUpload,
  onReadingStatus,
  onNotesChange,
  availableTags,
  onUpdateTags,
  onCreateTag,
  onUpdateTag,
  onDismiss,
  findSimilarDisabled,
  onRestore,
  onRelevanceNoteChange,
  onFlag,
  isRetrieving = false,
  isUploading = false,
  viewMode = "info",
  pdfViewerContent,
  fullTextViewerContent,
  explanationContent,
  onClosePdfViewer,
  onViewFullText,
  onMaximizePdf,
  pdfMaximized,
  onSmartTrawl,
  smartTrawlRunning,
  hasHighlights,
  evidenceClips,
  onViewClip,
  isHydrating = false,
  paperIndex,
  angles,
}: Props) {
  const [copied, setCopied] = useState(false);
  const [editingScore, setEditingScore] = useState(false);
  const [overrideValue, setOverrideValue] = useState("");
  const [summaryExpanded, setSummaryExpanded] = useState(true);
  const [extractingAbstract, setExtractingAbstract] = useState(false);
  const [promoting, setPromoting] = useState(false);
  // Copy-or-move and Flag open from the More menu as popovers.
  const [morePopover, setMorePopover] = useState<"route" | "flag" | null>(null);
  const pendingPopoverRef = useRef<"route" | "flag" | null>(null);
  const notesDebounceRef = useRef<ReturnType<typeof setTimeout>>(null);

  const debouncedNotesUpdate = useCallback(
    (html: string) => {
      if (notesDebounceRef.current) clearTimeout(notesDebounceRef.current);
      notesDebounceRef.current = setTimeout(() => {
        onNotesChange?.(paper.id, html);
      }, 1500);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- paper.id is stable within the detail pane
    [paper.id, onNotesChange],
  );

  const notesEditor = useEditor({
    extensions: [StarterKit],
    content: paper.notes || "<p></p>",
    onUpdate: ({ editor }) => {
      debouncedNotesUpdate(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class:
          "min-h-[60px] max-h-[200px] overflow-y-auto rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring prose prose-sm max-w-none",
      },
    },
  });

  // Track the last value we synced into the editor so a late-arriving hydrate
  // can refresh empty notes (slim → full) without clobbering user typing.
  const lastSyncedNotesRef = useRef<string>("");
  useEffect(() => {
    if (!notesEditor) return;
    const incoming = paper.notes || "<p></p>";
    if (notesEditor.getHTML() === lastSyncedNotesRef.current) {
      notesEditor.commands.setContent(incoming);
    }
    lastSyncedNotesRef.current = incoming;
  }, [paper.id, paper.notes]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (notesDebounceRef.current) clearTimeout(notesDebounceRef.current);
    };
  }, []);

  // Reset local loading states when paper changes, pdf_status updates, or retrieval completes
  // (PdfRow's own flags reset by remount — it is keyed on the same inputs.)
  useEffect(() => {
    setExtractingAbstract(false);
    setPromoting(false);
    setMorePopover(null);
  }, [paper.id, paper.canonical_pdf_status, isRetrieving]);

  const copyDoi = () => {
    if (paper.doi) {
      navigator.clipboard.writeText(paper.doi);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const isPdfView = viewMode === "pdf" && !!pdfViewerContent;
  const isFullTextView = viewMode === "fulltext" && !!fullTextViewerContent;
  const isContentView = isPdfView || isFullTextView;
  // Same thresholds as the table's 👍/👎 column and the "Marked relevant" filter.
  const isMarkedRelevant = paper.user_score_override != null && paper.user_score_override >= 70;
  const isMarkedNotRelevant = paper.user_score_override != null && paper.user_score_override <= 30;
  const chips = angles && angles.length >= 2 ? angleChips(paper, angles) : [];

  // More ▾ — the less frequent paper actions. Popover items queue their
  // popover until the menu has closed (see onCloseAutoFocus below).
  const canFetchAbstract = !!onExtractAbstract && !paper.abstract && (paper.abstract_status === "not_fetched" || paper.abstract_status === "failed");
  const moreItems = [
    conceptId && (
      <DropdownMenuItem key="route" onSelect={() => { pendingPopoverRef.current = "route"; }}>
        <ArrowRightLeft /> Copy or move to concept…
      </DropdownMenuItem>
    ),
    onFlag && (
      <DropdownMenuItem key="flag" onSelect={() => { pendingPopoverRef.current = "flag"; }}>
        <Flag /> Flag for later…
      </DropdownMenuItem>
    ),
    canFetchAbstract && (
      <DropdownMenuItem
        key="abstract"
        disabled={extractingAbstract}
        onSelect={() => { setExtractingAbstract(true); Promise.resolve(onExtractAbstract(paper.id)).finally(() => setExtractingAbstract(false)); }}
      >
        {extractingAbstract ? <Loader2 className="animate-spin" /> : <FileText />} Fetch abstract
      </DropdownMenuItem>
    ),
    onScoreOverride && paper.user_score_override != null && [
      <DropdownMenuSeparator key="sep" />,
      <DropdownMenuItem key="reset" onSelect={() => onScoreOverride(paper.id, null)}>
        <RotateCcw /> Reset to model score
      </DropdownMenuItem>,
    ],
  ].flat().filter(Boolean);

  return (
    <>
    {/* PDF view — mounted when pdfViewerContent exists, hidden when not active */}
    {pdfViewerContent && (
    <div className={`flex h-full flex-col border-l px-4${isPdfView ? "" : " hidden"}`}>
      <div className="flex items-center justify-between pb-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={onClosePdfViewer}
        >
          <ArrowUpRight className="size-3.5 rotate-[225deg]" />
          Back to Info
        </Button>
        <div className="flex items-center gap-1">
          {onSmartTrawl && hasHighlights && (
            <WhyDisabled reason={smartTrawlRunning ? "A Spot Trawl is already running." : null}>
            <Button
              variant="ghost"
              size="xs"
              onClick={onSmartTrawl}
              disabled={smartTrawlRunning}
              title="Trawl citations from this paper's highlights"
              className="text-amber-400 hover:text-amber-300"
              data-guide-target="smartTrawlButton"
            >
              <Sparkles className="size-3.5 mr-1" />
              <span className="text-xs">Spot Trawl</span>
            </Button>
            </WhyDisabled>
          )}
          {onMaximizePdf && (
            <Button
              variant="ghost"
              size="xs"
              onClick={onMaximizePdf}
              aria-label={pdfMaximized ? "Restore PDF" : "Expand PDF"}
              title={pdfMaximized ? "Restore" : "Expand vertically"}
            >
              {pdfMaximized ? <Minimize2 className="size-4" /> : <Maximize2 className="size-4" />}
            </Button>
          )}
          <Button
            variant="ghost"
            size="xs"
            onClick={onClose}
            aria-label="Close detail"
          >
            <X className="size-4" />
          </Button>
        </div>
      </div>
      <div className="flex-1 overflow-auto">
        {explanationContent}
        {pdfViewerContent}
      </div>
    </div>
    )}

    {/* Full text view — mounted when fullTextViewerContent exists, hidden when not active */}
    {fullTextViewerContent && (
    <div className={`flex h-full flex-col border-l px-4${isFullTextView ? "" : " hidden"}`}>
      <div className="flex items-center justify-between pb-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={onClosePdfViewer}
        >
          <ArrowUpRight className="size-3.5 rotate-[225deg]" />
          Back to Info
        </Button>
        <div className="flex items-center gap-1">
          {onMaximizePdf && (
            <Button
              variant="ghost"
              size="xs"
              onClick={onMaximizePdf}
              aria-label={pdfMaximized ? "Restore Full Text" : "Expand Full Text"}
              title={pdfMaximized ? "Restore" : "Expand vertically"}
            >
              {pdfMaximized ? <Minimize2 className="size-4" /> : <Maximize2 className="size-4" />}
            </Button>
          )}
          <Button
            variant="ghost"
            size="xs"
            onClick={onClose}
            aria-label="Close detail"
          >
            <X className="size-4" />
          </Button>
        </div>
      </div>
      <div className="flex-1 overflow-hidden">
        {explanationContent}
        {fullTextViewerContent}
      </div>
    </div>
    )}

    {/* Info view — hidden when content view is active */}
    <div className={`flex h-full flex-col overflow-y-auto border-l px-4${isContentView ? " hidden" : ""}`}>
      {/* Header */}
      <div className="flex items-start justify-between gap-2 pb-2">
        <h3 className="text-sm font-semibold leading-tight">{paper.title}</h3>
        <Button
          variant="ghost"
          size="xs"
          onClick={onClose}
          className="shrink-0"
          aria-label="Close detail"
        >
          <X className="size-4" />
        </Button>
      </div>

      <Separator />

      <div className="space-y-4 py-3 text-sm">
        {/* Authors + metadata compact block */}
        <div>
          {paper.authors.length > 0 && (
            <p className="text-xs text-muted-foreground">
              {paper.authors.join(", ")}{paper.year ? ` · ${paper.year}` : ""}{paper.journal ? ` · ${paper.journal}` : ""}
            </p>
          )}
          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
            {/* Scheme-checked: doi/url come from BibTeX + Zotero/Mendeley
                imports, so a poisoned record could otherwise put a
                `javascript:` payload one click away in the app origin
                (audit 2026-07-28, MEDIUM-5). Unsafe values render as plain
                text — a visible downgrade, not a silent redirect. */}
            {safeDoiHref(paper.doi) ? (
              <a
                href={safeDoiHref(paper.doi)}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                DOI
                <ExternalLink className="ml-0.5 inline size-3" />
              </a>
            ) : (
              paper.doi && <span className="text-muted-foreground">DOI</span>
            )}
            {safeExternalHref(paper.url) ? (
              <a
                href={safeExternalHref(paper.url)}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                S2
                <ExternalLink className="ml-0.5 inline size-3" />
              </a>
            ) : (
              paper.url && <span className="text-muted-foreground">S2</span>
            )}
          </div>
        </div>

        {/* Stats row */}
        <div className="flex flex-wrap items-center gap-2" data-guide-target="scoreOverrideControl">
          {paper.normalized_score > 0 && (
            <>
              {paper.user_score_override != null ? (
                <button
                  type="button"
                  className="inline-flex cursor-pointer items-center gap-1 border-none bg-transparent p-0"
                  onClick={() => {
                    if (onScoreOverride) {
                      setEditingScore(true);
                      setOverrideValue(String(paper.user_score_override));
                    }
                  }}
                  title={onScoreOverride ? "Click to change override" : undefined}
                >
                  <ScoreBadge label="Override" value={paper.user_score_override} isOverride />
                  <span className="text-xs text-muted-foreground">
                    LLM: {paper.normalized_score}
                  </span>
                </button>
              ) : (
                <button
                  type="button"
                  className="cursor-pointer border-none bg-transparent p-0"
                  onClick={() => {
                    if (onScoreOverride) {
                      setEditingScore(true);
                      setOverrideValue(String(paper.normalized_score));
                    }
                  }}
                  title={onScoreOverride ? "Click to override score" : undefined}
                >
                  <ScoreBadge label="Relevance" value={paper.normalized_score} />
                </button>
              )}
            </>
          )}
          {editingScore && (
            <span className="inline-flex items-center gap-1">
              <Input
                type="number"
                min={0}
                max={100}
                value={overrideValue}
                onChange={(e) => setOverrideValue(e.target.value)}
                className="h-6 w-16 text-xs"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    const v = Number(overrideValue);
                    if (v >= 0 && v <= 100 && onScoreOverride) {
                      onScoreOverride(paper.id, v);
                      setEditingScore(false);
                    }
                  } else if (e.key === "Escape") {
                    setEditingScore(false);
                  }
                }}
              />
              <Button
                variant="ghost"
                size="xs"
                onClick={() => {
                  const v = Number(overrideValue);
                  if (v >= 0 && v <= 100 && onScoreOverride) {
                    onScoreOverride(paper.id, v);
                    setEditingScore(false);
                  }
                }}
              >
                <Check className="size-3" />
              </Button>
              <Button
                variant="ghost"
                size="xs"
                onClick={() => setEditingScore(false)}
              >
                <X className="size-3" />
              </Button>
            </span>
          )}
          {paper.methodology_score > 0 && (
            <ScoreBadge
              label="Methodology"
              value={paper.methodology_score}
            />
          )}
          <span className="text-xs text-muted-foreground">
            {paper.is_influential && <Check className="inline size-3 text-green-500" />} {paper.citations} citations
          </span>
          {paper.contribution_type && (
            <Badge variant="outline">{paper.contribution_type}</Badge>
          )}
          {paper.argumentation_role && (
            <Badge variant="outline">{paper.argumentation_role}</Badge>
          )}
        </div>

        {/* Actions — three labelled rows: judge · the PDF (its own status) · the paper */}
        {onScoreOverride && (
          <div className="flex flex-wrap items-center gap-1" role="group" aria-label="Judge">
            {/* Binary judgement — same thresholds/toggle as the table's 👍/👎 column
                (>=70 relevant, <=30 not relevant). Exact scores: click the badge above. */}
            <Button
              variant="outline"
              size="xs"
              aria-pressed={isMarkedRelevant}
              onClick={() => onScoreOverride(paper.id, isMarkedRelevant ? null : 85)}
              className={isMarkedRelevant ? "border-green-500 text-green-600 dark:text-green-400" : undefined}
            >
              <ThumbsUp className={isMarkedRelevant ? "fill-green-500" : undefined} /> Relevant <Kbd>→</Kbd>
            </Button>
            <Button
              variant="outline"
              size="xs"
              aria-pressed={isMarkedNotRelevant}
              onClick={() => onScoreOverride(paper.id, isMarkedNotRelevant ? null : 15)}
              className={isMarkedNotRelevant ? "border-red-500 text-red-600 dark:text-red-400" : undefined}
            >
              <ThumbsDown className={isMarkedNotRelevant ? "fill-red-500" : undefined} /> Not relevant <Kbd>←</Kbd>
            </Button>
          </div>
        )}

        {(paper.doi || onDownloadPdf || onViewPdf || onUploadPdf) && (
          <PdfRow
            key={`${paper.id}:${paper.canonical_pdf_status}:${paper.pdf_available_source}:${isRetrieving}:${viewMode}`}
            paper={paper}
            isRetrieving={isRetrieving}
            isUploading={isUploading}
            onViewPdf={onViewPdf}
            onExpandPdf={onExpandPdf}
            onViewFullText={onViewFullText}
            onDownloadPdf={onDownloadPdf}
            onUploadPdf={onUploadPdf}
            onSetUpLibraryLink={onSetUpLibraryLink}
          />
        )}

        <div className="flex flex-wrap items-center gap-1" role="group" aria-label="Paper">
          {onPromote && paper.paper_role === "trawled" && (
            <Button variant="outline" size="xs" onClick={() => { setPromoting(true); Promise.resolve(onPromote(paper.id)).finally(() => setPromoting(false)); }} disabled={promoting}>
              {promoting ? <Loader2 className="animate-spin" /> : <ArrowUpRight />} Promote to seed
            </Button>
          )}
          {paper.doi && (
            <Button variant="outline" size="xs" onClick={copyDoi}>
              {copied ? <Check /> : <Copy />} {copied ? "Copied" : "Copy DOI"}
            </Button>
          )}
          {paper.exclusion_reason
            ? onRestore && (
                <Button variant="outline" size="xs" onClick={() => onRestore(paper.id)}>
                  <RotateCcw /> Restore
                </Button>
              )
            : onDismiss && (
                <DismissPopover labelled onDismiss={(reason, text, findSimilar) => onDismiss(paper.id, reason, text, findSimilar)} findSimilarDisabled={findSimilarDisabled} />
              )}
          {moreItems.length > 0 && (
            <span className="relative inline-flex">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="xs">
                    More <ChevronDown />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="start"
                  onCloseAutoFocus={(e) => {
                    // A popover opened from the menu takes focus itself; handing
                    // it back to More would dismiss the popover straight away.
                    if (pendingPopoverRef.current) {
                      e.preventDefault();
                      setMorePopover(pendingPopoverRef.current);
                      pendingPopoverRef.current = null;
                    }
                  }}
                >
                  {moreItems}
                </DropdownMenuContent>
              </DropdownMenu>
              {conceptId && (
                <RouteToConceptPopover
                  conceptId={conceptId}
                  paperId={paper.id}
                  isFoundational={paper.paper_role === "foundational"}
                  open={morePopover === "route"}
                  onOpenChange={(o) => setMorePopover(o ? "route" : null)}
                  anchor={<span aria-hidden className="pointer-events-none absolute inset-0" />}
                />
              )}
              {onFlag && (
                <FlagPopover
                  onFlag={(note) => onFlag(paper.id, note)}
                  open={morePopover === "flag"}
                  onOpenChange={(o) => setMorePopover(o ? "flag" : null)}
                  anchor={<span aria-hidden className="pointer-events-none absolute inset-0" />}
                />
              )}
            </span>
          )}
        </div>

        {/* Reading status + Tags */}
        <div className="space-y-2" data-guide-target="paperDetailTagsSection">
          {onReadingStatus && (
            <div className="flex items-center gap-2">
              <BookOpen className="size-3 text-muted-foreground" />
              <span className="text-xs text-muted-foreground">Reading:</span>
              {READING_STATUSES.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => onReadingStatus(paper.id, s)}
                  className={`rounded-full px-2 py-0.5 text-xs ${
                    paper.reading_status === s
                      ? s === "read"
                        ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
                        : s === "skimmed"
                          ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400"
                          : "bg-muted text-muted-foreground"
                      : "text-muted-foreground hover:bg-muted"
                  }`}
                >
                  {readingLabel(s)}
                </button>
              ))}
              {autoReadingNote(paper.reading_status, paper.reading_status_source) && (
                <span
                  data-testid="reading-auto-note"
                  className="text-xs text-muted-foreground"
                  title={`${autoReadingNote(paper.reading_status, paper.reading_status_source)} — click Unread to undo`}
                >
                  · set automatically
                </span>
              )}
            </div>
          )}
          {onUpdateTags ? (
            <TagPicker
              paperTags={paper.tags ?? []}
              availableTags={availableTags ?? []}
              onToggleTag={(tagId) => {
                const currentIds = (paper.tags ?? []).map((t) => t.id);
                const newIds = currentIds.includes(tagId)
                  ? currentIds.filter((id) => id !== tagId)
                  : [...currentIds, tagId];
                onUpdateTags(paper.id, newIds);
              }}
              onCreateTag={onCreateTag}
              onUpdateTag={onUpdateTag}
            />
          ) : (paper.tags ?? []).length > 0 ? (
            <div className="space-y-1">
              <p className="text-xs font-medium text-muted-foreground">Tags</p>
              <div className="flex flex-wrap gap-1">
                {paper.tags!.map((tag) => (
                  <span
                    key={tag.id}
                    className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium text-white"
                    style={{ backgroundColor: tag.color }}
                  >
                    {tag.name}
                  </span>
                ))}
              </div>
            </div>
          ) : null}
        </div>

        {/* Why it's a seed (persisted seed-discovery rationale, foundational only) */}
        {paper.paper_role === "foundational" &&
          paper.seed_discovery_metadata?.reason && (
            <div className="space-y-1">
              <p className="text-[10px] uppercase tracking-wide font-semibold text-muted-foreground/70">
                Why it's a seed
              </p>
              <p className="text-xs text-muted-foreground/90 leading-snug">
                {paper.seed_discovery_metadata.reason}
              </p>
              {!!paper.seed_discovery_metadata.overlap_score &&
                paper.seed_discovery_metadata.overlap_score >= 1 && (
                  <p className="text-[11px] text-emerald-400/90">
                    Cited by {paper.seed_discovery_metadata.overlap_score} other seed
                    {paper.seed_discovery_metadata.overlap_score === 1 ? "" : "s"}
                  </p>
                )}
            </div>
          )}

        {/* Discovery provenance — which seed/paper led PaperChase here */}
        {paperIndex && <DiscoveryProvenance links={paper.discovered_via} paperIndex={paperIndex} />}

        {/* Relevance note (foundational papers only) */}
        {paper.paper_role === "foundational" && onRelevanceNoteChange && (
          <RelevanceNoteEditor
            paperId={paper.id}
            note={paper.relevance_note ?? ""}
            onChange={onRelevanceNoteChange}
          />
        )}

        {/* Research notes */}
        {onNotesChange && (
          <div className="space-y-1">
            <p className="text-xs font-medium text-muted-foreground">
              Notes
              {isHydrating && !paper.notes && (
                <span className="ml-2 text-[10px] font-normal italic text-muted-foreground/70">
                  Loading…
                </span>
              )}
            </p>
            <EditorContent editor={notesEditor} />
          </div>
        )}

        {/* Evidence clips — persistent source='evidence' highlights from this
            paper. Violet matches the evidence color scheme (HighlightOverlay /
            Stash-as-Evidence button). */}
        {evidenceClips && evidenceClips.length > 0 && (
          <div>
            <p className="mb-1 text-xs font-medium text-muted-foreground">
              Evidence clips ({evidenceClips.length})
              {paper.has_evidence && (
                <span className="ml-2 rounded bg-violet-500/15 px-1 py-0.5 text-[10px] font-normal text-violet-400">
                  attached to claims
                </span>
              )}
            </p>
            <div className="space-y-1">
              {evidenceClips.map((clip) => (
                <button
                  key={clip.id}
                  type="button"
                  onClick={() => onViewClip?.(clip.page, clip.id)}
                  className="block w-full rounded border border-violet-500/30 bg-violet-500/5 px-2 py-1 text-left transition-colors hover:bg-violet-500/10"
                  title="View in PDF"
                >
                  <span className="line-clamp-2 text-xs leading-relaxed">
                    {clip.text}
                  </span>
                  {clip.page != null && (
                    <span className="mt-0.5 block text-[10px] text-muted-foreground">
                      Page {clip.page + 1}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Abstract */}
        {paper.abstract ? (
          <div>
            <p className="mb-1 text-xs font-medium text-muted-foreground">
              Abstract
            </p>
            <p className="text-xs leading-relaxed">{paper.abstract}</p>
          </div>
        ) : isHydrating ? (
          <div>
            <p className="mb-1 text-xs font-medium text-muted-foreground">
              Abstract
              <span className="ml-2 text-[10px] font-normal italic text-muted-foreground/70">
                Loading…
              </span>
            </p>
            <div className="space-y-1.5" aria-label="Loading abstract">
              <div className="h-2.5 w-full animate-pulse rounded bg-muted" />
              <div className="h-2.5 w-[92%] animate-pulse rounded bg-muted" />
              <div className="h-2.5 w-[78%] animate-pulse rounded bg-muted" />
            </div>
          </div>
        ) : (
          <p className="text-xs italic text-muted-foreground">
            No abstract available
          </p>
        )}

        {/* Cross-concept suggestions */}
        {conceptId && (
          <CrossConceptSuggestions conceptId={conceptId} paperId={paper.id} />
        )}

        {/* AI Summary */}
        {paper.ai_summary ? (
          <div>
            <button
              type="button"
              className="mb-1 flex items-center gap-1 text-xs font-medium text-muted-foreground"
              onClick={() => setSummaryExpanded(!summaryExpanded)}
            >
              {summaryExpanded ? (
                <ChevronDown className="size-3" />
              ) : (
                <ChevronRight className="size-3" />
              )}
              <Sparkles className="size-3" />
              AI Summary
            </button>
            {summaryExpanded && (
              <div>
                <p className="text-xs leading-relaxed">{paper.ai_summary}</p>
                <div className="mt-1 flex items-center gap-1">
                  {onSummarize && (
                    <Button
                      variant="ghost"
                      size="xs"
                      onClick={() => onSummarize(paper.id)}
                      disabled={isSummarizing}
                    >
                      {isSummarizing ? (
                        <Loader2 className="mr-1 size-3 animate-spin" />
                      ) : (
                        <RefreshCw className="mr-1 size-3" />
                      )}
                      Regenerate
                    </Button>
                  )}
                  {/* Show when the current summary is canonical but the user has
                      uploaded their own PDF — regenerate against the upload so
                      the summary reflects the copy they actually have access to. */}
                  {onRegenerateFromUpload &&
                    summarySource === "canonical" &&
                    hasUserUpload && (
                      <WhyDisabled reason={isSummarizing ? "A summary is being written." : null}>
                      <Button
                        variant="ghost"
                        size="xs"
                        onClick={() => onRegenerateFromUpload(paper.id)}
                        disabled={isSummarizing}
                        title="Re-summarize using the PDF you uploaded"
                      >
                        <RefreshCw className="mr-1 size-3" />
                        Regenerate from your upload
                      </Button>
                      </WhyDisabled>
                    )}
                </div>
              </div>
            )}
          </div>
        ) : onSummarize ? (
          <Button
            variant="outline"
            size="sm"
            onClick={() => onSummarize(paper.id)}
            disabled={isSummarizing}
          >
            {isSummarizing ? (
              <Loader2 className="size-3.5 animate-spin" />
            ) : (
              <Sparkles className="size-3.5" />
            )}
            {isSummarizing ? "Summarizing..." : "Summarize"}
          </Button>
        ) : null}

        {/* Scoring rationale */}
        {paper.relevance_justification || chips.length > 0 ? (
          <div>
            <p className="mb-1 text-xs font-medium text-muted-foreground">
              {chips.length > 0 ? "Why it's here" : "Scoring Rationale"}
            </p>
            {paper.relevance_justification && (
              <p className="text-xs text-muted-foreground leading-relaxed">{paper.relevance_justification}</p>
            )}
            {chips.length > 0 && (
              <div className="mt-1.5 flex flex-wrap gap-1">
                {chips.map((c) => (
                  <span key={c.id} className={`rounded-full border px-2 py-0.5 text-[11px] tabular-nums ${
                    c.score >= 40 ? "border-primary/40 text-foreground" : "text-muted-foreground"
                  }`}>{`${c.label} ${c.score}`}</span>
                ))}
              </div>
            )}
          </div>
        ) : isHydrating ? (
          <div>
            <p className="mb-1 text-xs font-medium text-muted-foreground">
              Scoring Rationale
              <span className="ml-2 text-[10px] font-normal italic text-muted-foreground/70">
                Loading…
              </span>
            </p>
            <div className="space-y-1.5" aria-label="Loading scoring rationale">
              <div className="h-2.5 w-full animate-pulse rounded bg-muted" />
              <div className="h-2.5 w-[85%] animate-pulse rounded bg-muted" />
            </div>
          </div>
        ) : null}

      </div>
    </div>
    </>
  );
}

function TagPicker({
  paperTags,
  availableTags,
  onToggleTag,
  onCreateTag,
  onUpdateTag,
}: {
  paperTags: PaperTag[];
  availableTags: PaperTag[];
  onToggleTag: (tagId: string) => void;
  onCreateTag?: (data: { name: string; color: string }) => void;
  onUpdateTag?: (tagId: string, data: { name?: string; color?: string }) => void;
}) {
  const [showPicker, setShowPicker] = useState(false);
  const [newTagName, setNewTagName] = useState("");
  const [newTagColor, setNewTagColor] = useState(TAG_COLORS[0]);
  const [editingTagId, setEditingTagId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [editColor, setEditColor] = useState("");
  const pickerRef = useRef<HTMLDivElement>(null);

  const assignedIds = new Set(paperTags.map((t) => t.id));

  const handleCreate = () => {
    const trimmed = newTagName.trim();
    if (!trimmed || !onCreateTag) return;
    onCreateTag({ name: trimmed, color: newTagColor });
    setNewTagName("");
  };

  const startEdit = (tag: PaperTag, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingTagId(tag.id);
    setEditName(tag.name);
    setEditColor(tag.color);
  };

  const commitEdit = () => {
    if (!editingTagId || !onUpdateTag) return;
    const trimmed = editName.trim();
    if (!trimmed) { setEditingTagId(null); return; }
    const tag = availableTags.find((t) => t.id === editingTagId);
    if (tag && (trimmed !== tag.name || editColor !== tag.color)) {
      onUpdateTag(editingTagId, { name: trimmed, color: editColor });
    }
    setEditingTagId(null);
  };

  // Close picker on outside click
  useEffect(() => {
    if (!showPicker) return;
    const handler = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
        setShowPicker(false);
        setEditingTagId(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showPicker]);

  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-1.5">
        <Tag className="size-3 text-muted-foreground" />
        <span className="text-xs text-muted-foreground">Tags</span>
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        {paperTags.map((tag) => (
          <span
            key={tag.id}
            className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs"
            style={{ backgroundColor: tag.color + "20", color: tag.color }}
          >
            <span
              className="inline-block size-2 rounded-full"
              style={{ backgroundColor: tag.color }}
            />
            {tag.name}
            <button
              type="button"
              onClick={() => onToggleTag(tag.id)}
              className="rounded-full hover:bg-black/10 dark:hover:bg-white/20"
              title={`Remove "${tag.name}"`}
            >
              <X className="size-3" />
            </button>
          </span>
        ))}
        <div className="relative" ref={pickerRef}>
          <button
            type="button"
            onClick={() => setShowPicker(!showPicker)}
            className="inline-flex items-center gap-0.5 rounded-full border border-dashed px-2 py-0.5 text-xs text-muted-foreground hover:border-primary hover:text-primary"
          >
            <Plus className="size-3" />
            Tag
          </button>
          {showPicker && (
            <div className="absolute left-0 top-7 z-30 w-56 rounded-md border bg-popover p-2 shadow-md">
              {availableTags.length > 0 && (
                <div className="mb-2 max-h-40 space-y-0.5 overflow-y-auto">
                  {availableTags.map((tag) =>
                    editingTagId === tag.id ? (
                      <div key={tag.id} className="space-y-1.5 rounded bg-accent/50 p-1.5">
                        <input
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") commitEdit();
                            if (e.key === "Escape") setEditingTagId(null);
                          }}
                          className="w-full rounded border bg-background px-1.5 py-1 text-xs outline-none"
                          maxLength={50}
                          autoFocus
                        />
                        <div className="flex items-center gap-1">
                          {TAG_COLORS.map((c) => (
                            <button
                              key={c}
                              type="button"
                              onClick={() => setEditColor(c)}
                              className={`size-3.5 rounded-full ${editColor === c ? "ring-2 ring-primary ring-offset-1" : ""}`}
                              style={{ backgroundColor: c }}
                            />
                          ))}
                          <button
                            type="button"
                            onClick={commitEdit}
                            className="ml-auto rounded bg-primary px-1.5 py-0.5 text-[10px] text-primary-foreground"
                          >
                            Save
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div
                        key={tag.id}
                        className="group flex w-full items-center gap-2 rounded px-2 py-1 text-xs hover:bg-accent"
                      >
                        <button
                          type="button"
                          onClick={() => onToggleTag(tag.id)}
                          className="flex flex-1 items-center gap-2"
                        >
                          <span
                            className="inline-block size-2.5 rounded-full"
                            style={{ backgroundColor: tag.color }}
                          />
                          <span className="flex-1 text-left">{tag.name}</span>
                          {assignedIds.has(tag.id) && (
                            <Check className="size-3 text-primary" />
                          )}
                        </button>
                        {onUpdateTag && (
                          <button
                            type="button"
                            onClick={(e) => startEdit(tag, e)}
                            className="hidden size-4 items-center justify-center rounded text-muted-foreground hover:text-foreground group-hover:inline-flex"
                            title="Edit tag"
                          >
                            <Pencil className="size-2.5" />
                          </button>
                        )}
                      </div>
                    ),
                  )}
                </div>
              )}
              {onCreateTag && (
                <div className="border-t pt-2">
                  <div className="flex gap-1">
                    <input
                      value={newTagName}
                      onChange={(e) => setNewTagName(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && handleCreate()}
                      placeholder="New tag..."
                      className="flex-1 rounded border bg-background px-1.5 py-1 text-xs outline-none"
                      maxLength={50}
                    />
                    <button
                      type="button"
                      onClick={handleCreate}
                      disabled={!newTagName.trim()}
                      className="rounded bg-primary px-2 py-1 text-xs text-primary-foreground disabled:opacity-50"
                    >
                      Add
                    </button>
                  </div>
                  <div className="mt-1.5 flex gap-1">
                    {TAG_COLORS.map((c) => (
                      <button
                        key={c}
                        type="button"
                        onClick={() => setNewTagColor(c)}
                        className={`size-4 rounded-full ${newTagColor === c ? "ring-2 ring-primary ring-offset-1" : ""}`}
                        style={{ backgroundColor: c }}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ScoreBadge({ label, value, isOverride }: { label: string; value: number; isOverride?: boolean }) {
  let color = "bg-muted text-muted-foreground";
  if (isOverride) {
    color = "bg-blue-100 text-blue-800 ring-1 ring-blue-300 dark:bg-blue-900/30 dark:text-blue-400 dark:ring-blue-700";
  } else if (value >= 80) {
    color = "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400";
  } else if (value >= 50) {
    color = "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400";
  }

  return (
    <span className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium ${color}`}>
      {label}: {value}
    </span>
  );
}

function RelevanceNoteEditor({
  paperId,
  note,
  onChange,
}: {
  paperId: string;
  note: string;
  onChange: (paperId: string, note: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(note);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    setDraft(note);
    setEditing(false);
  }, [note]);

  useEffect(() => {
    if (editing) inputRef.current?.select();
  }, [editing]);

  const save = () => {
    const trimmed = draft.trim();
    if (trimmed !== note) {
      onChange(paperId, trimmed);
    }
    setEditing(false);
  };

  return (
    <div className="space-y-1">
      <p className="text-xs font-medium text-muted-foreground">Relevance Note</p>
      {editing ? (
        <div className="flex flex-col gap-1">
          <textarea
            ref={inputRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); save(); }
              if (e.key === "Escape") { setDraft(note); setEditing(false); }
            }}
            onBlur={save}
            rows={2}
            className="w-full resize-none rounded-md border border-input bg-background px-2 py-1 text-xs leading-relaxed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
        </div>
      ) : (
        <p
          className="cursor-pointer rounded px-1 py-0.5 text-xs leading-relaxed text-muted-foreground hover:bg-muted"
          onClick={() => setEditing(true)}
          title="Click to edit"
        >
          {note || <span className="italic">No relevance note — click to add</span>}
          <Pencil className="ml-1 inline size-2.5 align-middle opacity-0 transition-opacity group-hover:opacity-100" />
        </p>
      )}
    </div>
  );
}

function Kbd({ children }: { children: React.ReactNode }) {
  return <kbd className="rounded border px-1 font-sans text-[10px] leading-none text-muted-foreground">{children}</kbd>;
}
